import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { clearSubmitMark, shouldForceCloseOnRender } from "../lib/modal_submit_mark"
import { blankIds, track } from "../lib/track_usage"

// A failed submit re-renders the form into the frame within this long of
// the submit; same window as modal_submit_mark's.
const RESUBMIT_WINDOW_MS = 10_000

export default class extends Controller {
  connect() {
    this.modal = new Modal(this.element)
    this.modal.show()
    this.trackOpen()
  }

  // Logged here, not on the frame GET: Turbo's hover prefetch fetches the
  // modal without a click. A failed submit renders the form back into the
  // same frame, which reconnects this controller on a new element — the
  // frame remembers the submit so that re-render is not a second open.
  // `form` is the frame's path with ids (and their slugs, 12-some-org)
  // blanked, so "which forms get opened" has bounded cardinality.
  trackOpen() {
    const frame = this.element.closest("turbo-frame")
    const resubmitted = !!frame && Date.now() - (frame._usageSubmittedAt || 0) < RESUBMIT_WINDOW_MS
    const src = frame?.getAttribute("src")
    const form = src && blankIds(new URL(src, location.href).pathname)
    const props = form ? { form } : {}
    if (frame) {
      delete frame._usageSubmittedAt
      this.element.addEventListener("turbo:submit-start", () => { frame._usageSubmittedAt = Date.now() })
      // The pair to modal_opened: opened minus submitted is the abandonment rate per form.
      this.element.addEventListener("turbo:submit-end", event => {
        if (!event.detail.success) return
        delete frame._usageSubmittedAt
        track("form_submitted", props)
      })
    }
    if (resubmitted) return

    track("modal_opened", props)
  }

  hideBeforeRender(event) {
    if (!this.isOpen()) { return }

    // Same rule as base_modal.js#_hideBeforeRender (both controllers sit on
    // this element): a morph only closes the modal whose own form just
    // submitted — the element-level mark is written by BaseModal's submit
    // listener. Unmarked open modals are data-turbo-permanent and survive
    // the morph. Full-page renders still close everything.
    if (!shouldForceCloseOnRender(this.element, event)) { return }

    clearSubmitMark(this.element)
    event.preventDefault()
    this.element.addEventListener('hidden.bs.modal', event.detail.resume)
    // Render-forced close, not a user dismissal — skip BaseModal's
    // dirty-form confirm (see base_modal.js#_hideBeforeRender).
    this.element._skipDirtyConfirm = true
    this.modal.hide()
    this.element._skipDirtyConfirm = false
  }

  isOpen() {
    return this.element.classList.contains("show")
  }
}
